import { Button, Dropdown, IconButton, Modal } from "@alm/flow-ds/components";
import CardColumn from "components/CardColumn";
import api from "lib/api";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { exportFile } from "utils/exportFile";
import { View } from "./Views";

interface Props extends View {
	deleteView: (id: string, definitionId: string) => void;
}

export default function ViewCard({
	_id,
	viewLabel,
	description,
	deleteView,
}: Props) {
	const { t } = useTranslation();

	const [isWarningOpen, setIsWarningOpen] = useState(false);
	const [warning, setWarning] = useState<string | undefined>();

	const definitionId = _id.substring(
		_id.indexOf("definitions/") + "definitions/".length
	);

	// Downloads the json from view service
	const handleDownload = useCallback(async () => {
		try {
			const { data } = await api.view.getView(definitionId);
			const json = JSON.stringify(data.data, undefined, 4);
			exportFile(definitionId, json, "json");
		} catch (e) {
			console.error(e);
		}
	}, [definitionId]);

	const handlePreDelete = useCallback(() => {
		setWarning("confirmViewDelete");
		setIsWarningOpen(true);
	}, []);

	const handleDelete = useCallback(async () => {
		deleteView(_id, definitionId);
		setIsWarningOpen(false);
	}, [deleteView, _id, definitionId]);

	return (
		<>
			<CardColumn className="view-card__id text-truncate" width="sm">
				{definitionId}
			</CardColumn>
			<CardColumn className="view-card__title text-truncate" width="md">
				{viewLabel}
			</CardColumn>
			<CardColumn
				className="view-card__description text-truncate"
				width="xl">
				{description}
			</CardColumn>
			<CardColumn className="view-card__menu">
				<Dropdown
					id={`view-card__dropdown__${definitionId}`}
					menuBtn={
						<IconButton
							className="btn-basic--dark"
							icon="ellipsis-v"
							label={t("main.labels.menu")}
						/>
					}
					align="right">
					<Button
						name="download"
						label={t("common.labels.download")}
						className="dropdown-item btn-basic--default"
						onClick={handleDownload}
					/>
					<Button
						name="delete"
						label={t("common.labels.delete")}
						className="dropdown-item btn-basic--default"
						onClick={handlePreDelete}
					/>
				</Dropdown>
			</CardColumn>
			{isWarningOpen && (
				<Modal
					title={t(`alerts.${warning}.title`, { view: definitionId })}
					isOpen={isWarningOpen}
					close={() => setIsWarningOpen(false)}
					footer={
						warning === "confirmViewDelete" ? (
							<>
								<Button
									className="btn-outline-primary"
									onClick={() => setIsWarningOpen(false)}
									label={t("common.labels.cancel")}
								/>
								<Button
									className="btn-danger"
									onClick={handleDelete}
									label={t("common.labels.delete")}
								/>
							</>
						) : (
							<Button
								className="btn-primary"
								onClick={() => setIsWarningOpen(false)}
								label={t("common.labels.ok")}
							/>
						)
					}
				/>
			)}
		</>
	);
}
